import { Route } from '@angular/router';
import { UiBreadcrumbItem, UiPageChromeConfig } from '@system-dyg/shared/ui';
import { demoDatabaseChrome } from './pages/documentation/database/demo-database.chrome';

const loadAdminShell = () =>
  import('./shell/admin-shell.component').then(m => m.AdminShellComponent);

const loadPendingSection = () =>
  import('./pages/module-panel/admin-module-panel.component').then(
    m => m.AdminModulePanelComponent,
  );

// ── Piezas de breadcrumb ────────────────────────────────────────────────────

const ADMIN_CRUMB: UiBreadcrumbItem = { id: 'admin', label: 'Configuraciones' };

const DASHBOARD_CRUMBS: readonly UiBreadcrumbItem[] = [
  ADMIN_CRUMB,
  { id: 'dashboard', label: 'Panel de control' },
];

const COMPANIES_CRUMBS: readonly UiBreadcrumbItem[] = [
  ADMIN_CRUMB,
  { id: 'companies', label: 'Empresas' },
  { id: 'companies-list', label: 'Empresas' },
];

const CATALOGS_CRUMBS: readonly UiBreadcrumbItem[] = [
  ADMIN_CRUMB,
  { id: 'catalogs', label: 'Catálogos' },
];

const COST_CENTERS_SECTION: UiBreadcrumbItem = {
  id: 'cost-centers',
  label: 'Centros de costo',
};
const COST_CENTERS_CRUMBS: readonly UiBreadcrumbItem[] = [
  ...CATALOGS_CRUMBS,
  COST_CENTERS_SECTION,
  { id: 'cost-centers-list', label: 'Centros de costo' },
];
const TYPES_CRUMBS: readonly UiBreadcrumbItem[] = [
  ...CATALOGS_CRUMBS,
  COST_CENTERS_SECTION,
  { id: 'types', label: 'Tipos' },
];

const AREAS_SECTION: UiBreadcrumbItem = { id: 'areas', label: 'Áreas' };
const AREAS_CRUMBS: readonly UiBreadcrumbItem[] = [
  ...CATALOGS_CRUMBS,
  AREAS_SECTION,
  { id: 'areas-list', label: 'Áreas' },
];

const SUPPLIES_SECTION: UiBreadcrumbItem = { id: 'supplies', label: 'Insumos' };
const SUPPLIES_CRUMBS: readonly UiBreadcrumbItem[] = [
  ...CATALOGS_CRUMBS,
  SUPPLIES_SECTION,
  { id: 'supplies-list', label: 'Insumos' },
];
const SUPPLY_GROUPS_CRUMBS: readonly UiBreadcrumbItem[] = [
  ...CATALOGS_CRUMBS,
  SUPPLIES_SECTION,
  { id: 'groups', label: 'Grupos' },
];
const SUPPLY_TYPES_CRUMBS: readonly UiBreadcrumbItem[] = [
  ...CATALOGS_CRUMBS,
  SUPPLIES_SECTION,
  { id: 'supply-types', label: 'Tipos' },
];
const SUPPLY_UNITS_CRUMBS: readonly UiBreadcrumbItem[] = [
  ...CATALOGS_CRUMBS,
  SUPPLIES_SECTION,
  { id: 'supply-units', label: 'Unidades de medida' },
];

const ACCOUNTING_ACCOUNTS_SECTION: UiBreadcrumbItem = {
  id: 'accounting-accounts',
  label: 'Cuentas contables',
};
const ACCOUNTING_ACCOUNTS_CRUMBS: readonly UiBreadcrumbItem[] = [
  ...CATALOGS_CRUMBS,
  ACCOUNTING_ACCOUNTS_SECTION,
  { id: 'accounting-accounts-list', label: 'Cuentas contables' },
];
const ACCOUNTING_STRUCTURE_CRUMBS: readonly UiBreadcrumbItem[] = [
  ...CATALOGS_CRUMBS,
  ACCOUNTING_ACCOUNTS_SECTION,
  { id: 'structure', label: 'Estructura' },
];

const FISCAL_SECTION: UiBreadcrumbItem = { id: 'fiscal', label: 'Fiscal' };
const TAX_REGIMES_CRUMBS: readonly UiBreadcrumbItem[] = [
  ...CATALOGS_CRUMBS,
  FISCAL_SECTION,
  { id: 'tax-regimes', label: 'Regimenes' },
];
const TAXES_CRUMBS: readonly UiBreadcrumbItem[] = [
  ...CATALOGS_CRUMBS,
  FISCAL_SECTION,
  { id: 'taxes', label: 'Impuestos' },
];
const OBLIGATIONS_CRUMBS: readonly UiBreadcrumbItem[] = [
  ...CATALOGS_CRUMBS,
  FISCAL_SECTION,
  { id: 'obligations', label: 'Obligaciones' },
];

const CURRENCIES_SECTION: UiBreadcrumbItem = { id: 'currencies', label: 'Divisas' };
const CURRENCIES_CRUMBS: readonly UiBreadcrumbItem[] = [
  ...CATALOGS_CRUMBS,
  CURRENCIES_SECTION,
  { id: 'currencies-list', label: 'Divisas' },
];

const FILES_SECTION: UiBreadcrumbItem = { id: 'files', label: 'Expedientes' };
const FILES_COMPANIES_CRUMBS: readonly UiBreadcrumbItem[] = [
  ...CATALOGS_CRUMBS,
  FILES_SECTION,
  { id: 'companies', label: 'Empresas' },
];
const FILES_EMPLOYEES_CRUMBS: readonly UiBreadcrumbItem[] = [
  ...CATALOGS_CRUMBS,
  FILES_SECTION,
  { id: 'employees', label: 'Empleados' },
];
const FILES_SUPPLIERS_CRUMBS: readonly UiBreadcrumbItem[] = [
  ...CATALOGS_CRUMBS,
  FILES_SECTION,
  { id: 'suppliers', label: 'Proveedores' },
];

// ── Secciones sin contenido propio ──────────────────────────────────────────

type PendingSection = {
  /** Módulo al que pertenece: resuelve sidebar y menubar. */
  moduleId: string;
  /** Etiqueta del módulo en el breadcrumb. */
  moduleLabel: string;
  /** Etiqueta de la sección: título y último nivel del breadcrumb. */
  sectionLabel: string;
  /** Ítem del menubar que queda activo, si la sección tiene uno. */
  activeMenubarId?: string;
};

/**
 * Ruta de una sección cuyo contenido está pendiente: solo declara su marco de
 * página y monta un componente vacío.
 */
function pendingSectionRoute(path: string, section: PendingSection): Route {
  const sectionId = path.slice(path.lastIndexOf('/') + 1);
  return {
    path,
    pathMatch: 'full',
    loadComponent: loadPendingSection,
    data: {
      chrome: {
        moduleId: section.moduleId,
        activeMenubarId: section.activeMenubarId ?? null,
        title: section.sectionLabel,
        titleBarMode: 'form',
        breadcrumbItems: [
          ADMIN_CRUMB,
          { id: section.moduleId, label: section.moduleLabel },
          { id: sectionId, label: section.sectionLabel },
        ],
      } satisfies UiPageChromeConfig,
    },
  };
}

const DASHBOARD_SECTION = {
  moduleId: 'dashboard',
  moduleLabel: 'Panel de control',
} as const;

const DOC_SECTION = {
  moduleId: 'documentation',
  moduleLabel: 'Documentación',
} as const;

// ── Edición de empresa ──────────────────────────────────────────────────────

const COMPANY_EDIT_CHROME = {
  moduleId: 'companies',
  activeMenubarId: 'com-companies',
  title: 'Editar empresa',
  titleBarMode: 'form',
  breadcrumbItems: [...COMPANIES_CRUMBS, { id: 'edit', label: 'Editar empresa' }],
} satisfies UiPageChromeConfig;

/** Pestañas de la edición de empresa como rutas hijas del host. */
const COMPANY_EDIT_ROUTE: Route = {
  path: 'companies/companies/:companyId/edit',
  loadComponent: () =>
    import('./pages/companies/companies/company-edit/company-edit.component').then(
      m => m.CompanyEditComponent,
    ),
  data: { chrome: COMPANY_EDIT_CHROME },
  children: [
    { path: '', pathMatch: 'full', redirectTo: 'general' },
    {
      path: 'general',
      loadComponent: () =>
        import(
          './pages/companies/companies/company-edit/general/company-general.component'
        ).then(m => m.CompanyGeneralComponent),
    },
    {
      path: 'legal',
      loadComponent: () =>
        import(
          './pages/companies/companies/company-edit/legal/company-legal.component'
        ).then(m => m.CompanyLegalComponent),
      children: [
        { path: '', pathMatch: 'full', redirectTo: 'articles-of-incorporation' },
        {
          path: 'articles-of-incorporation',
          loadComponent: () =>
            import(
              './pages/companies/companies/company-edit/legal/articles-of-incorporation/articles-of-incorporation.component'
            ).then(m => m.ArticlesOfIncorporationComponent),
        },
        {
          path: 'assembly-minutes',
          loadComponent: () =>
            import(
              './pages/companies/companies/company-edit/legal/assembly-minutes/assembly-minutes.component'
            ).then(m => m.AssemblyMinutesComponent),
        },
        {
          path: 'financial-statements',
          pathMatch: 'full',
          loadComponent: () =>
            import(
              './pages/companies/companies/company-edit/legal/financial-statements/financial-statements.component'
            ).then(m => m.FinancialStatementsComponent),
        },
      ],
    },
    {
      path: 'digital-file',
      loadComponent: () =>
        import(
          './pages/companies/companies/company-edit/digital-file/company-digital-file.component'
        ).then(m => m.CompanyDigitalFileComponent),
    },
    {
      path: 'shcp',
      loadComponent: () =>
        import(
          './pages/companies/companies/company-edit/tax/tax-authority-shell/tax-authority-shell.component'
        ).then(m => m.TaxAuthorityShellComponent),
      data: {
        taxAuthority: {
          defaultSection: 'regime',
          ariaLabel: 'Secciones SHCP',
          menuItems: [
            { value: 'regime', label: 'Régimen' },
            { value: 'taxes', label: 'Impuestos' },
            { value: 'obligations', label: 'Obligaciones' },
          ],
        },
      },
      children: [
        { path: '', pathMatch: 'full', redirectTo: 'regime' },
        {
          path: 'regime',
          loadComponent: () =>
            import(
              './pages/companies/companies/company-edit/tax/shcp/regime/tax-regime.component'
            ).then(m => m.TaxRegimeComponent),
        },
        {
          path: 'taxes',
          loadComponent: () =>
            import(
              './pages/companies/companies/company-edit/tax/shcp/taxes/tax-taxes.component'
            ).then(m => m.TaxTaxesComponent),
        },
        {
          path: 'obligations',
          loadComponent: () =>
            import(
              './pages/companies/companies/company-edit/tax/shcp/obligations/tax-obligations.component'
            ).then(m => m.TaxObligationsComponent),
        },
      ],
    },
    {
      path: 'imss-infonavit',
      loadComponent: () =>
        import(
          './pages/companies/companies/company-edit/tax/tax-authority-shell/tax-authority-shell.component'
        ).then(m => m.TaxAuthorityShellComponent),
      data: {
        taxAuthority: {
          defaultSection: 'employer-registers',
          ariaLabel: 'Secciones IMSS - INFONAVIT',
          menuItems: [
            { value: 'employer-registers', label: 'Registros patronales' },
            { value: 'siroc', label: 'SIROC' },
            { value: 'obligations', label: 'Obligaciones' },
          ],
        },
      },
      children: [
        { path: '', pathMatch: 'full', redirectTo: 'employer-registers' },
        {
          path: 'employer-registers',
          loadComponent: () =>
            import(
              './pages/companies/companies/company-edit/tax/section-placeholder/tax-section-placeholder.component'
            ).then(m => m.TaxSectionPlaceholderComponent),
        },
        {
          path: 'siroc',
          loadComponent: () =>
            import(
              './pages/companies/companies/company-edit/tax/section-placeholder/tax-section-placeholder.component'
            ).then(m => m.TaxSectionPlaceholderComponent),
        },
        {
          path: 'obligations',
          loadComponent: () =>
            import(
              './pages/companies/companies/company-edit/tax/imss-infonavit/obligations/tax-obligations.component'
            ).then(m => m.ImssInfonavitTaxObligationsComponent),
        },
      ],
    },
    {
      path: 'state',
      loadComponent: () =>
        import(
          './pages/companies/companies/company-edit/tax/tax-authority-shell/tax-authority-shell.component'
        ).then(m => m.TaxAuthorityShellComponent),
      data: {
        taxAuthority: {
          defaultSection: 'registers',
          ariaLabel: 'Secciones Estado',
          menuItems: [
            { value: 'registers', label: 'Registros' },
            { value: 'taxes', label: 'Impuestos' },
            { value: 'obligations', label: 'Obligaciones' },
          ],
        },
      },
      children: [
        { path: '', pathMatch: 'full', redirectTo: 'registers' },
        {
          path: 'registers',
          loadComponent: () =>
            import(
              './pages/companies/companies/company-edit/tax/section-placeholder/tax-section-placeholder.component'
            ).then(m => m.TaxSectionPlaceholderComponent),
        },
        {
          path: 'taxes',
          loadComponent: () =>
            import(
              './pages/companies/companies/company-edit/tax/state/taxes/tax-taxes.component'
            ).then(m => m.StateTaxTaxesComponent),
        },
        {
          path: 'obligations',
          loadComponent: () =>
            import(
              './pages/companies/companies/company-edit/tax/state/obligations/tax-obligations.component'
            ).then(m => m.StateTaxObligationsComponent),
        },
      ],
    },
    {
      path: 'municipality',
      loadComponent: () =>
        import(
          './pages/companies/companies/company-edit/tax/tax-authority-shell/tax-authority-shell.component'
        ).then(m => m.TaxAuthorityShellComponent),
      data: {
        taxAuthority: {
          defaultSection: 'registers',
          ariaLabel: 'Secciones Municipio',
          menuItems: [
            { value: 'registers', label: 'Registros' },
            { value: 'taxes', label: 'Impuestos' },
            { value: 'obligations', label: 'Obligaciones' },
          ],
        },
      },
      children: [
        { path: '', pathMatch: 'full', redirectTo: 'registers' },
        {
          path: 'registers',
          loadComponent: () =>
            import(
              './pages/companies/companies/company-edit/tax/section-placeholder/tax-section-placeholder.component'
            ).then(m => m.TaxSectionPlaceholderComponent),
        },
        {
          path: 'taxes',
          loadComponent: () =>
            import(
              './pages/companies/companies/company-edit/tax/municipality/taxes/tax-taxes.component'
            ).then(m => m.MunicipalityTaxTaxesComponent),
        },
        {
          path: 'obligations',
          loadComponent: () =>
            import(
              './pages/companies/companies/company-edit/tax/municipality/obligations/tax-obligations.component'
            ).then(m => m.MunicipalityTaxObligationsComponent),
        },
      ],
    },
    {
      path: 'stps',
      loadComponent: () =>
        import(
          './pages/companies/companies/company-edit/tax/tax-authority-shell/tax-authority-shell.component'
        ).then(m => m.TaxAuthorityShellComponent),
      data: {
        taxAuthority: {
          defaultSection: 'registers',
          ariaLabel: 'Secciones STPS',
          menuItems: [
            { value: 'registers', label: 'Registros' },
            { value: 'obligations', label: 'Obligaciones' },
          ],
        },
      },
      children: [
        { path: '', pathMatch: 'full', redirectTo: 'registers' },
        {
          path: 'registers',
          loadComponent: () =>
            import(
              './pages/companies/companies/company-edit/tax/section-placeholder/tax-section-placeholder.component'
            ).then(m => m.TaxSectionPlaceholderComponent),
        },
        {
          path: 'obligations',
          loadComponent: () =>
            import(
              './pages/companies/companies/company-edit/tax/stps/obligations/tax-obligations.component'
            ).then(m => m.StpsTaxObligationsComponent),
        },
      ],
    },
    {
      path: 'files',
      loadComponent: () =>
        import(
          './pages/companies/companies/company-edit/files/company-files.component'
        ).then(m => m.CompanyFilesComponent),
    },
    {
      path: 'action-log',
      loadComponent: () =>
        import(
          './pages/companies/companies/company-edit/action-log/company-action-log.component'
        ).then(m => m.CompanyActionLogComponent),
    },
  ],
};

export const appRoutes: Route[] = [
  // ── Redirecciones ─────────────────────────────────────────────────────────
  // Van antes del shell: resuelven la URL sin montar ninguna página.
  { path: '', pathMatch: 'full', redirectTo: 'dashboard/general' },
  { path: 'dashboard', pathMatch: 'full', redirectTo: 'dashboard/general' },
  { path: 'companies', pathMatch: 'full', redirectTo: 'companies/companies' },
  { path: 'companies/dashboard', pathMatch: 'full', redirectTo: 'companies/companies' },
  { path: 'users', pathMatch: 'full', redirectTo: 'users/users' },
  { path: 'users/dashboard', pathMatch: 'full', redirectTo: 'users/users' },
  { path: 'roles', pathMatch: 'full', redirectTo: 'roles/roles' },
  { path: 'roles/dashboard', pathMatch: 'full', redirectTo: 'roles/roles' },
  { path: 'inventory', pathMatch: 'full', redirectTo: 'inventory/projects/projects' },
  { path: 'inventory/dashboard', pathMatch: 'full', redirectTo: 'inventory/projects/projects' },
  { path: 'inventory/projects', pathMatch: 'full', redirectTo: 'inventory/projects/projects' },
  { path: 'documentation', pathMatch: 'full', redirectTo: 'documentation/general' },
  { path: 'documentation/dashboard', pathMatch: 'full', redirectTo: 'documentation/general' },
  { path: 'catalogs', pathMatch: 'full', redirectTo: 'catalogs/cost-centers/cost-centers' },
  { path: 'documentation/reglas-de-negocio', pathMatch: 'full', redirectTo: 'documentation/business-rules' },
  { path: 'documentation/procesos', pathMatch: 'full', redirectTo: 'documentation/processes' },
  { path: 'documentation/base-de-datos', pathMatch: 'full', redirectTo: 'documentation/database' },
  { path: 'documentation/infraestructura', pathMatch: 'full', redirectTo: 'documentation/infrastructure' },
  { path: 'documentation/seguridad', pathMatch: 'full', redirectTo: 'documentation/security' },

  {
    // App Shell: única instancia del layout. Las páginas solo aportan contenido
    // y declaran su marco en `data.chrome`.
    path: '',
    loadComponent: loadAdminShell,
    children: [
      // ── Panel de control ──────────────────────────────────────────────────
      {
        path: 'dashboard/general',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/dashboard/general/general-dashboard.component').then(
            m => m.GeneralDashboardComponent,
          ),
        data: {
          chrome: {
            moduleId: 'dashboard',
            title: 'General',
            titleBarMode: 'form',
            breadcrumbItems: [...DASHBOARD_CRUMBS, { id: 'general', label: 'General' }],
          } satisfies UiPageChromeConfig,
        },
      },
      pendingSectionRoute('dashboard/companies', {
        ...DASHBOARD_SECTION,
        sectionLabel: 'Empresas',
        activeMenubarId: 'companies',
      }),
      pendingSectionRoute('dashboard/users', {
        ...DASHBOARD_SECTION,
        sectionLabel: 'Usuarios',
        activeMenubarId: 'users',
      }),
      pendingSectionRoute('dashboard/roles', {
        ...DASHBOARD_SECTION,
        sectionLabel: 'Roles',
        activeMenubarId: 'roles',
      }),
      pendingSectionRoute('dashboard/inventory', {
        ...DASHBOARD_SECTION,
        sectionLabel: 'Inventario',
        activeMenubarId: 'inventory',
      }),
      pendingSectionRoute('dashboard/catalogs', {
        ...DASHBOARD_SECTION,
        sectionLabel: 'Catálogos',
        activeMenubarId: 'catalogs',
      }),
      pendingSectionRoute('dashboard/documentation', {
        ...DASHBOARD_SECTION,
        sectionLabel: 'Documentación',
        activeMenubarId: 'documentation',
      }),

      // ── Empresas ──────────────────────────────────────────────────────────
      {
        path: 'companies/companies',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/companies/companies/companies.component').then(
            m => m.CompaniesComponent,
          ),
        data: {
          chrome: {
            moduleId: 'companies',
            activeMenubarId: 'com-companies',
            title: 'Empresas',
            titleBarMode: 'data-table',
            breadcrumbItems: COMPANIES_CRUMBS,
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'companies/companies/new',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/companies/companies/company-form/company-form.component').then(
            m => m.CompanyFormComponent,
          ),
        data: {
          chrome: {
            moduleId: 'companies',
            activeMenubarId: 'com-companies',
            title: 'Nueva empresa',
            titleBarMode: 'form',
            breadcrumbItems: [
              ...COMPANIES_CRUMBS,
              { id: 'new', label: 'Nueva empresa' },
            ],
          } satisfies UiPageChromeConfig,
        },
      },
      COMPANY_EDIT_ROUTE,

      // ── Usuarios ──────────────────────────────────────────────────────────
      {
        path: 'users/users',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/users/users/users.component').then(m => m.UsersComponent),
        data: {
          chrome: {
            moduleId: 'users',
            activeMenubarId: 'usr-users',
            title: 'Usuarios',
            titleBarMode: 'data-table',
            breadcrumbItems: [
              ADMIN_CRUMB,
              { id: 'users', label: 'Usuarios' },
              { id: 'users-list', label: 'Usuarios' },
            ],
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'users/users/new',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/users/users/user-form/user-form.component').then(
            m => m.UserFormComponent,
          ),
        data: {
          chrome: {
            moduleId: 'users',
            activeMenubarId: 'usr-users',
            title: 'Nuevo usuario',
            titleBarMode: 'form',
            breadcrumbItems: [
              ADMIN_CRUMB,
              { id: 'users', label: 'Usuarios' },
              { id: 'users-list', label: 'Usuarios' },
              { id: 'new', label: 'Nuevo usuario' },
            ],
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'users/users/:userId/edit',
        loadComponent: () =>
          import('./pages/users/users/user-edit/user-edit.component').then(
            m => m.UserEditComponent,
          ),
        data: {
          chrome: {
            moduleId: 'users',
            activeMenubarId: 'usr-users',
            title: 'Editar usuario',
            titleBarMode: 'form',
            breadcrumbItems: [
              ADMIN_CRUMB,
              { id: 'users', label: 'Usuarios' },
              { id: 'users-list', label: 'Usuarios' },
              { id: 'edit', label: 'Editar usuario' },
            ],
          } satisfies UiPageChromeConfig,
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/users/users/user-edit/general/user-edit-general.component'
              ).then(m => m.UserEditGeneralComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/users/users/user-edit/files/user-edit-files.component'
              ).then(m => m.UserEditFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/users/users/user-edit/action-log/user-edit-action-log.component'
              ).then(m => m.UserEditActionLogComponent),
          },
        ],
      },

      // ── Roles ─────────────────────────────────────────────────────────────
      {
        path: 'roles/roles',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/roles/roles/roles.component').then(m => m.RolesComponent),
        data: {
          chrome: {
            moduleId: 'roles',
            activeMenubarId: 'rol-roles',
            title: 'Roles',
            titleBarMode: 'data-table',
            breadcrumbItems: [
              ADMIN_CRUMB,
              { id: 'roles', label: 'Roles' },
              { id: 'roles-list', label: 'Roles' },
            ],
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'roles/roles/new',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/roles/roles/role-form/role-form.component').then(
            m => m.RoleFormComponent,
          ),
        data: {
          chrome: {
            moduleId: 'roles',
            activeMenubarId: 'rol-roles',
            title: 'Nuevo rol',
            titleBarMode: 'form',
            breadcrumbItems: [
              ADMIN_CRUMB,
              { id: 'roles', label: 'Roles' },
              { id: 'roles-list', label: 'Roles' },
              { id: 'new', label: 'Nuevo rol' },
            ],
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'roles/roles/:roleId/edit',
        loadComponent: () =>
          import('./pages/roles/roles/role-edit/role-edit.component').then(
            m => m.RoleEditComponent,
          ),
        data: {
          chrome: {
            moduleId: 'roles',
            activeMenubarId: 'rol-roles',
            title: 'Editar rol',
            titleBarMode: 'form',
            breadcrumbItems: [
              ADMIN_CRUMB,
              { id: 'roles', label: 'Roles' },
              { id: 'roles-list', label: 'Roles' },
              { id: 'edit', label: 'Editar rol' },
            ],
          } satisfies UiPageChromeConfig,
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/roles/roles/role-edit/general/role-edit-general.component'
              ).then(m => m.RoleEditGeneralComponent),
          },
          {
            path: 'permissions',
            loadComponent: () =>
              import(
                './pages/roles/roles/role-edit/permissions/role-edit-permissions.component'
              ).then(m => m.RoleEditPermissionsComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/roles/roles/role-edit/files/role-edit-files.component'
              ).then(m => m.RoleEditFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/roles/roles/role-edit/action-log/role-edit-action-log.component'
              ).then(m => m.RoleEditActionLogComponent),
          },
        ],
      },

      // ── Inventario ────────────────────────────────────────────────────────
      {
        path: 'inventory/projects/projects',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/inventory/projects/projects/projects.component').then(
            m => m.ProjectsComponent,
          ),
        data: {
          chrome: {
            moduleId: 'inventory',
            activeMenubarId: 'inv-projects',
            title: 'Proyectos',
            titleBarMode: 'data-table',
            breadcrumbItems: [
              ADMIN_CRUMB,
              { id: 'inventory', label: 'Inventario' },
              { id: 'projects', label: 'Proyectos' },
            ],
          } satisfies UiPageChromeConfig,
        },
      },

      // ── Catálogos ─────────────────────────────────────────────────────────
      {
        path: 'catalogs/cost-centers/cost-centers',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/catalogs/cost-centers/cost-centers/cost-centers.component'
          ).then(m => m.CostCentersComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-cost-centers-list',
            title: 'Centros de costo',
            titleBarMode: 'data-table',
            breadcrumbItems: COST_CENTERS_CRUMBS,
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'catalogs/cost-centers/cost-centers/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/catalogs/cost-centers/cost-centers/cost-center-form/cost-center-form.component'
          ).then(m => m.CostCenterFormComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-cost-centers-list',
            title: 'Nuevo centro de costo',
            titleBarMode: 'form',
            breadcrumbItems: [
              ...COST_CENTERS_CRUMBS,
              { id: 'new', label: 'Nuevo centro de costo' },
            ],
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'catalogs/cost-centers/cost-centers/:costCenterId/edit',
        loadComponent: () =>
          import(
            './pages/catalogs/cost-centers/cost-centers/cost-center-edit/cost-center-edit.component'
          ).then(m => m.CostCenterEditComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-cost-centers-list',
            title: 'Editar centro de costo',
            titleBarMode: 'form',
            breadcrumbItems: [
              ...COST_CENTERS_CRUMBS,
              { id: 'edit', label: 'Editar centro de costo' },
            ],
          } satisfies UiPageChromeConfig,
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/catalogs/cost-centers/cost-centers/cost-center-edit/general/cost-center-general.component'
              ).then(m => m.CostCenterGeneralComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/catalogs/cost-centers/cost-centers/cost-center-edit/files/cost-center-files.component'
              ).then(m => m.CostCenterFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/catalogs/cost-centers/cost-centers/cost-center-edit/action-log/cost-center-action-log.component'
              ).then(m => m.CostCenterActionLogComponent),
          },
        ],
      },
      {
        path: 'catalogs/cost-centers/types',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/catalogs/cost-centers/types/types.component').then(
            m => m.TypesComponent,
          ),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-cost-types',
            title: 'Tipos',
            titleBarMode: 'data-table',
            breadcrumbItems: TYPES_CRUMBS,
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'catalogs/cost-centers/types/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/catalogs/cost-centers/types/type-form/type-form.component'
          ).then(m => m.TypeFormComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-cost-types',
            title: 'Nuevo tipo',
            titleBarMode: 'form',
            breadcrumbItems: [...TYPES_CRUMBS, { id: 'new', label: 'Nuevo tipo' }],
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'catalogs/cost-centers/types/:typeId/edit',
        loadComponent: () =>
          import(
            './pages/catalogs/cost-centers/types/type-edit/type-edit.component'
          ).then(m => m.TypeEditComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-cost-types',
            title: 'Editar tipo',
            titleBarMode: 'form',
            breadcrumbItems: [
              ...TYPES_CRUMBS,
              { id: 'edit', label: 'Editar tipo' },
            ],
          } satisfies UiPageChromeConfig,
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/catalogs/cost-centers/types/type-edit/general/type-general.component'
              ).then(m => m.TypeGeneralComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/catalogs/cost-centers/types/type-edit/files/type-files.component'
              ).then(m => m.TypeFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/catalogs/cost-centers/types/type-edit/action-log/type-action-log.component'
              ).then(m => m.TypeActionLogComponent),
          },
        ],
      },
      {
        path: 'catalogs/areas/areas',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/catalogs/areas/areas/areas.component').then(
            m => m.AreasComponent,
          ),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-area-areas',
            title: 'Áreas',
            titleBarMode: 'data-table',
            breadcrumbItems: AREAS_CRUMBS,
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'catalogs/areas/areas/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/catalogs/areas/areas/area-form/area-form.component'
          ).then(m => m.AreaFormComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-area-areas',
            title: 'Nueva área',
            titleBarMode: 'form',
            breadcrumbItems: [
              ...AREAS_CRUMBS,
              { id: 'new', label: 'Nueva área' },
            ],
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'catalogs/areas/areas/:areaId/edit',
        loadComponent: () =>
          import(
            './pages/catalogs/areas/areas/area-edit/area-edit.component'
          ).then(m => m.AreaEditComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-area-areas',
            title: 'Editar área',
            titleBarMode: 'form',
            breadcrumbItems: [
              ...AREAS_CRUMBS,
              { id: 'edit', label: 'Editar área' },
            ],
          } satisfies UiPageChromeConfig,
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/catalogs/areas/areas/area-edit/general/area-general.component'
              ).then(m => m.AreaGeneralComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/catalogs/areas/areas/area-edit/files/area-files.component'
              ).then(m => m.AreaFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/catalogs/areas/areas/area-edit/action-log/area-action-log.component'
              ).then(m => m.AreaActionLogComponent),
          },
        ],
      },
      {
        path: 'catalogs/supplies/supplies',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/catalogs/supplies/supplies/supplies.component').then(
            m => m.SuppliesComponent,
          ),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-supplies-list',
            title: 'Insumos',
            titleBarMode: 'data-table',
            breadcrumbItems: SUPPLIES_CRUMBS,
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'catalogs/supplies/supplies/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/catalogs/supplies/supplies/supply-form/supply-form.component'
          ).then(m => m.SupplyFormComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-supplies-list',
            title: 'Nuevo insumo',
            titleBarMode: 'form',
            breadcrumbItems: [
              ...SUPPLIES_CRUMBS,
              { id: 'new', label: 'Nuevo insumo' },
            ],
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'catalogs/supplies/supplies/:supplyId/edit',
        loadComponent: () =>
          import(
            './pages/catalogs/supplies/supplies/supply-edit/supply-edit.component'
          ).then(m => m.SupplyEditComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-supplies-list',
            title: 'Editar insumo',
            titleBarMode: 'form',
            breadcrumbItems: [
              ...SUPPLIES_CRUMBS,
              { id: 'edit', label: 'Editar insumo' },
            ],
          } satisfies UiPageChromeConfig,
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/catalogs/supplies/supplies/supply-edit/general/supply-general.component'
              ).then(m => m.SupplyGeneralComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/catalogs/supplies/supplies/supply-edit/files/supply-files.component'
              ).then(m => m.SupplyFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/catalogs/supplies/supplies/supply-edit/action-log/supply-action-log.component'
              ).then(m => m.SupplyActionLogComponent),
          },
        ],
      },
      {
        path: 'catalogs/supplies/groups',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/catalogs/supplies/groups/groups.component').then(
            m => m.GroupsComponent,
          ),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-groups',
            title: 'Grupos',
            titleBarMode: 'data-table',
            breadcrumbItems: SUPPLY_GROUPS_CRUMBS,
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'catalogs/supplies/groups/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/catalogs/supplies/groups/group-form/group-form.component'
          ).then(m => m.GroupFormComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-groups',
            title: 'Nuevo grupo',
            titleBarMode: 'form',
            breadcrumbItems: [
              ...SUPPLY_GROUPS_CRUMBS,
              { id: 'new', label: 'Nuevo grupo' },
            ],
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'catalogs/supplies/groups/:groupId/edit',
        loadComponent: () =>
          import(
            './pages/catalogs/supplies/groups/group-edit/group-edit.component'
          ).then(m => m.GroupEditComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-groups',
            title: 'Editar grupo',
            titleBarMode: 'form',
            breadcrumbItems: [
              ...SUPPLY_GROUPS_CRUMBS,
              { id: 'edit', label: 'Editar grupo' },
            ],
          } satisfies UiPageChromeConfig,
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/catalogs/supplies/groups/group-edit/general/group-general.component'
              ).then(m => m.GroupGeneralComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/catalogs/supplies/groups/group-edit/files/group-files.component'
              ).then(m => m.GroupFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/catalogs/supplies/groups/group-edit/action-log/group-action-log.component'
              ).then(m => m.GroupActionLogComponent),
          },
        ],
      },
      {
        path: 'catalogs/supplies/types',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/catalogs/supplies/types/types.component').then(
            m => m.SupplyTypesComponent,
          ),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-supply-types',
            title: 'Tipos',
            titleBarMode: 'data-table',
            breadcrumbItems: SUPPLY_TYPES_CRUMBS,
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'catalogs/supplies/types/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/catalogs/supplies/types/type-form/type-form.component'
          ).then(m => m.SupplyTypeFormComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-supply-types',
            title: 'Nuevo tipo',
            titleBarMode: 'form',
            breadcrumbItems: [
              ...SUPPLY_TYPES_CRUMBS,
              { id: 'new', label: 'Nuevo tipo' },
            ],
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'catalogs/supplies/types/:typeId/edit',
        loadComponent: () =>
          import(
            './pages/catalogs/supplies/types/supply-type-edit/supply-type-edit.component'
          ).then(m => m.SupplyTypeEditComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-supply-types',
            title: 'Editar tipo',
            titleBarMode: 'form',
            breadcrumbItems: [
              ...SUPPLY_TYPES_CRUMBS,
              { id: 'edit', label: 'Editar tipo' },
            ],
          } satisfies UiPageChromeConfig,
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/catalogs/supplies/types/supply-type-edit/general/supply-type-general.component'
              ).then(m => m.SupplyTypeGeneralComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/catalogs/supplies/types/supply-type-edit/files/supply-type-files.component'
              ).then(m => m.SupplyTypeFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/catalogs/supplies/types/supply-type-edit/action-log/supply-type-action-log.component'
              ).then(m => m.SupplyTypeActionLogComponent),
          },
        ],
      },
      {
        path: 'catalogs/supplies/measurement-units',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/catalogs/supplies/measurement-units/measurement-units.component'
          ).then(m => m.MeasurementUnitsComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-supply-units',
            title: 'Unidades de medida',
            titleBarMode: 'data-table',
            breadcrumbItems: SUPPLY_UNITS_CRUMBS,
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'catalogs/supplies/measurement-units/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/catalogs/supplies/measurement-units/unit-form/unit-form.component'
          ).then(m => m.MeasurementUnitFormComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-supply-units',
            title: 'Nueva unidad de medida',
            titleBarMode: 'form',
            breadcrumbItems: [
              ...SUPPLY_UNITS_CRUMBS,
              { id: 'new', label: 'Nueva unidad de medida' },
            ],
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'catalogs/supplies/measurement-units/:unitId/edit',
        loadComponent: () =>
          import(
            './pages/catalogs/supplies/measurement-units/unit-edit/unit-edit.component'
          ).then(m => m.MeasurementUnitEditComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-supply-units',
            title: 'Editar unidad de medida',
            titleBarMode: 'form',
            breadcrumbItems: [
              ...SUPPLY_UNITS_CRUMBS,
              { id: 'edit', label: 'Editar unidad de medida' },
            ],
          } satisfies UiPageChromeConfig,
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/catalogs/supplies/measurement-units/unit-edit/general/unit-general.component'
              ).then(m => m.MeasurementUnitGeneralComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/catalogs/supplies/measurement-units/unit-edit/files/unit-files.component'
              ).then(m => m.MeasurementUnitFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/catalogs/supplies/measurement-units/unit-edit/action-log/unit-action-log.component'
              ).then(m => m.MeasurementUnitActionLogComponent),
          },
        ],
      },
      {
        path: 'catalogs/files/companies',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/catalogs/files/companies/files-companies.component'
          ).then(m => m.FilesCompaniesComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-files-companies',
            title: 'Empresas',
            titleBarMode: 'data-table',
            breadcrumbItems: FILES_COMPANIES_CRUMBS,
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'catalogs/files/companies/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/catalogs/files/companies/files-company-form/files-company-form.component'
          ).then(m => m.FilesCompanyFormComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-files-companies',
            title: 'Nuevo documento',
            titleBarMode: 'form',
            breadcrumbItems: [
              ...FILES_COMPANIES_CRUMBS,
              { id: 'new', label: 'Nuevo documento' },
            ],
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'catalogs/files/companies/:companyFileId/edit',
        loadComponent: () =>
          import(
            './pages/catalogs/files/companies/files-company-edit/files-company-edit.component'
          ).then(m => m.FilesCompanyEditComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-files-companies',
            title: 'Editar documento',
            titleBarMode: 'form',
            breadcrumbItems: [
              ...FILES_COMPANIES_CRUMBS,
              { id: 'edit', label: 'Editar documento' },
            ],
          } satisfies UiPageChromeConfig,
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/catalogs/files/companies/files-company-edit/general/files-company-general.component'
              ).then(m => m.FilesCompanyGeneralComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/catalogs/files/companies/files-company-edit/files/files-company-files.component'
              ).then(m => m.FilesCompanyFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/catalogs/files/companies/files-company-edit/action-log/files-company-action-log.component'
              ).then(m => m.FilesCompanyActionLogComponent),
          },
        ],
      },
      {
        path: 'catalogs/files/employees',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/catalogs/files/employees/files-employees.component'
          ).then(m => m.FilesEmployeesComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-files-employees',
            title: 'Empleados',
            titleBarMode: 'data-table',
            breadcrumbItems: FILES_EMPLOYEES_CRUMBS,
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'catalogs/files/employees/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/catalogs/files/employees/files-employee-form/files-employee-form.component'
          ).then(m => m.FilesEmployeeFormComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-files-employees',
            title: 'Nuevo documento',
            titleBarMode: 'form',
            breadcrumbItems: [
              ...FILES_EMPLOYEES_CRUMBS,
              { id: 'new', label: 'Nuevo documento' },
            ],
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'catalogs/files/employees/:employeeFileId/edit',
        loadComponent: () =>
          import(
            './pages/catalogs/files/employees/files-employee-edit/files-employee-edit.component'
          ).then(m => m.FilesEmployeeEditComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-files-employees',
            title: 'Editar documento',
            titleBarMode: 'form',
            breadcrumbItems: [
              ...FILES_EMPLOYEES_CRUMBS,
              { id: 'edit', label: 'Editar documento' },
            ],
          } satisfies UiPageChromeConfig,
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/catalogs/files/employees/files-employee-edit/general/files-employee-general.component'
              ).then(m => m.FilesEmployeeGeneralComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/catalogs/files/employees/files-employee-edit/files/files-employee-files.component'
              ).then(m => m.FilesEmployeeFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/catalogs/files/employees/files-employee-edit/action-log/files-employee-action-log.component'
              ).then(m => m.FilesEmployeeActionLogComponent),
          },
        ],
      },
      {
        path: 'catalogs/files/suppliers',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/catalogs/files/suppliers/files-suppliers.component'
          ).then(m => m.FilesSuppliersComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-files-suppliers',
            title: 'Proveedores',
            titleBarMode: 'data-table',
            breadcrumbItems: FILES_SUPPLIERS_CRUMBS,
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'catalogs/files/suppliers/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/catalogs/files/suppliers/files-supplier-form/files-supplier-form.component'
          ).then(m => m.FilesSupplierFormComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-files-suppliers',
            title: 'Nuevo documento',
            titleBarMode: 'form',
            breadcrumbItems: [
              ...FILES_SUPPLIERS_CRUMBS,
              { id: 'new', label: 'Nuevo documento' },
            ],
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'catalogs/files/suppliers/:supplierFileId/edit',
        loadComponent: () =>
          import(
            './pages/catalogs/files/suppliers/files-supplier-edit/files-supplier-edit.component'
          ).then(m => m.FilesSupplierEditComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-files-suppliers',
            title: 'Editar documento',
            titleBarMode: 'form',
            breadcrumbItems: [
              ...FILES_SUPPLIERS_CRUMBS,
              { id: 'edit', label: 'Editar documento' },
            ],
          } satisfies UiPageChromeConfig,
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/catalogs/files/suppliers/files-supplier-edit/general/files-supplier-general.component'
              ).then(m => m.FilesSupplierGeneralComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/catalogs/files/suppliers/files-supplier-edit/files/files-supplier-files.component'
              ).then(m => m.FilesSupplierFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/catalogs/files/suppliers/files-supplier-edit/action-log/files-supplier-action-log.component'
              ).then(m => m.FilesSupplierActionLogComponent),
          },
        ],
      },
      {
        path: 'catalogs/accounting-accounts/accounting-accounts',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/catalogs/accounting-accounts/accounting-accounts/accounting-accounts.component'
          ).then(m => m.AccountingAccountsComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-accounting-accounts-list',
            title: 'Cuentas contables',
            titleBarMode: 'form',
            breadcrumbItems: ACCOUNTING_ACCOUNTS_CRUMBS,
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'catalogs/accounting-accounts/structure',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/catalogs/accounting-accounts/structure/structure.component'
          ).then(m => m.AccountingAccountStructureComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-accounting-accounts-structure',
            title: 'Estructura',
            titleBarMode: 'form',
            breadcrumbItems: ACCOUNTING_STRUCTURE_CRUMBS,
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'catalogs/fiscal/tax-regimes',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/catalogs/fiscal/tax-regimes/tax-regimes.component'
          ).then(m => m.TaxRegimesComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-fiscal-regimenes',
            title: 'Regimenes',
            titleBarMode: 'data-table',
            breadcrumbItems: TAX_REGIMES_CRUMBS,
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'catalogs/fiscal/tax-regimes/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/catalogs/fiscal/tax-regimes/tax-regime-form/tax-regime-form.component'
          ).then(m => m.TaxRegimeFormComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-fiscal-regimenes',
            title: 'Nuevo regimen',
            titleBarMode: 'form',
            breadcrumbItems: [
              ...TAX_REGIMES_CRUMBS,
              { id: 'new', label: 'Nuevo regimen' },
            ],
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'catalogs/fiscal/tax-regimes/:taxRegimeId/edit',
        loadComponent: () =>
          import(
            './pages/catalogs/fiscal/tax-regimes/tax-regime-edit/tax-regime-edit.component'
          ).then(m => m.TaxRegimeEditComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-fiscal-regimenes',
            title: 'Editar régimen',
            titleBarMode: 'form',
            breadcrumbItems: [
              ...TAX_REGIMES_CRUMBS,
              { id: 'edit', label: 'Editar régimen' },
            ],
          } satisfies UiPageChromeConfig,
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/catalogs/fiscal/tax-regimes/tax-regime-edit/general/tax-regime-general.component'
              ).then(m => m.TaxRegimeGeneralComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/catalogs/fiscal/tax-regimes/tax-regime-edit/files/tax-regime-files.component'
              ).then(m => m.TaxRegimeFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/catalogs/fiscal/tax-regimes/tax-regime-edit/action-log/tax-regime-action-log.component'
              ).then(m => m.TaxRegimeActionLogComponent),
          },
        ],
      },
      {
        path: 'catalogs/fiscal/taxes',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/catalogs/fiscal/taxes/taxes.component').then(
            m => m.TaxesComponent,
          ),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-fiscal-impuestos',
            title: 'Impuestos',
            titleBarMode: 'data-table',
            breadcrumbItems: TAXES_CRUMBS,
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'catalogs/fiscal/taxes/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/catalogs/fiscal/taxes/tax-form/tax-form.component'
          ).then(m => m.TaxFormComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-fiscal-impuestos',
            title: 'Nuevo impuesto',
            titleBarMode: 'form',
            breadcrumbItems: [
              ...TAXES_CRUMBS,
              { id: 'new', label: 'Nuevo impuesto' },
            ],
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'catalogs/fiscal/taxes/:taxId/edit',
        loadComponent: () =>
          import(
            './pages/catalogs/fiscal/taxes/tax-edit/tax-edit.component'
          ).then(m => m.TaxEditComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-fiscal-impuestos',
            title: 'Editar impuesto',
            titleBarMode: 'form',
            breadcrumbItems: [
              ...TAXES_CRUMBS,
              { id: 'edit', label: 'Editar impuesto' },
            ],
          } satisfies UiPageChromeConfig,
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/catalogs/fiscal/taxes/tax-edit/general/tax-edit-general.component'
              ).then(m => m.TaxEditGeneralComponent),
          },
          { path: 'values', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'rates',
            loadComponent: () =>
              import(
                './pages/catalogs/fiscal/taxes/tax-edit/values/tax-edit-values.component'
              ).then(m => m.TaxEditValuesComponent),
            data: { tipoCalculo: 'Tasa' },
          },
          {
            path: 'rates/:periodId',
            loadComponent: () =>
              import(
                './pages/catalogs/fiscal/taxes/tax-edit/values/tax-edit-values.component'
              ).then(m => m.TaxEditValuesComponent),
            data: { tipoCalculo: 'Tasa' },
          },
          {
            path: 'quotas',
            loadComponent: () =>
              import(
                './pages/catalogs/fiscal/taxes/tax-edit/values/tax-edit-values.component'
              ).then(m => m.TaxEditValuesComponent),
            data: { tipoCalculo: 'Cuota' },
          },
          {
            path: 'quotas/:periodId',
            loadComponent: () =>
              import(
                './pages/catalogs/fiscal/taxes/tax-edit/values/tax-edit-values.component'
              ).then(m => m.TaxEditValuesComponent),
            data: { tipoCalculo: 'Cuota' },
          },
          {
            path: 'tariffs',
            loadComponent: () =>
              import(
                './pages/catalogs/fiscal/taxes/tax-edit/values/tax-edit-values.component'
              ).then(m => m.TaxEditValuesComponent),
            data: { tipoCalculo: 'Tarifa' },
          },
          {
            path: 'tariffs/:periodId',
            loadComponent: () =>
              import(
                './pages/catalogs/fiscal/taxes/tax-edit/values/tax-edit-values.component'
              ).then(m => m.TaxEditValuesComponent),
            data: { tipoCalculo: 'Tarifa' },
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/catalogs/fiscal/taxes/tax-edit/files/tax-edit-files.component'
              ).then(m => m.TaxEditFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/catalogs/fiscal/taxes/tax-edit/action-log/tax-edit-action-log.component'
              ).then(m => m.TaxEditActionLogComponent),
          },
        ],
      },
      {
        path: 'catalogs/fiscal/obligations',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/catalogs/fiscal/obligations/obligations.component'
          ).then(m => m.ObligationsComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-fiscal-obligaciones',
            title: 'Obligaciones',
            titleBarMode: 'data-table',
            breadcrumbItems: OBLIGATIONS_CRUMBS,
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'catalogs/fiscal/obligations/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/catalogs/fiscal/obligations/obligation-form/obligation-form.component'
          ).then(m => m.ObligationFormComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-fiscal-obligaciones',
            title: 'Nueva obligación',
            titleBarMode: 'form',
            breadcrumbItems: [
              ...OBLIGATIONS_CRUMBS,
              { id: 'new', label: 'Nueva obligación' },
            ],
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'catalogs/fiscal/obligations/:obligationId/edit',
        loadComponent: () =>
          import(
            './pages/catalogs/fiscal/obligations/obligation-edit/obligation-edit.component'
          ).then(m => m.ObligationEditComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-fiscal-obligaciones',
            title: 'Editar obligación',
            titleBarMode: 'form',
            breadcrumbItems: [
              ...OBLIGATIONS_CRUMBS,
              { id: 'edit', label: 'Editar obligación' },
            ],
          } satisfies UiPageChromeConfig,
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/catalogs/fiscal/obligations/obligation-edit/general/obligation-edit-general.component'
              ).then(m => m.ObligationEditGeneralComponent),
          },
          {
            path: 'periods',
            loadComponent: () =>
              import(
                './pages/catalogs/fiscal/obligations/obligation-edit/periods/obligation-edit-periods.component'
              ).then(m => m.ObligationEditPeriodsComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/catalogs/fiscal/obligations/obligation-edit/files/obligation-edit-files.component'
              ).then(m => m.ObligationEditFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/catalogs/fiscal/obligations/obligation-edit/action-log/obligation-edit-action-log.component'
              ).then(m => m.ObligationEditActionLogComponent),
          },
        ],
      },
      {
        path: 'catalogs/currencies/currencies',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/catalogs/currencies/currencies/currencies.component'
          ).then(m => m.CurrenciesComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-currencies',
            title: 'Divisas',
            titleBarMode: 'data-table',
            breadcrumbItems: CURRENCIES_CRUMBS,
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'catalogs/currencies/currencies/new',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/catalogs/currencies/currencies/currency-form/currency-form.component'
          ).then(m => m.CurrencyFormComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-currencies',
            title: 'Nueva divisa',
            titleBarMode: 'form',
            breadcrumbItems: [
              ...CURRENCIES_CRUMBS,
              { id: 'new', label: 'Nueva divisa' },
            ],
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'catalogs/currencies/currencies/:currencyId/edit',
        loadComponent: () =>
          import(
            './pages/catalogs/currencies/currencies/currency-edit/currency-edit.component'
          ).then(m => m.CurrencyEditComponent),
        data: {
          chrome: {
            moduleId: 'catalogs',
            activeMenubarId: 'cat-currencies',
            title: 'Editar divisa',
            titleBarMode: 'form',
            breadcrumbItems: [
              ...CURRENCIES_CRUMBS,
              { id: 'edit', label: 'Editar divisa' },
            ],
          } satisfies UiPageChromeConfig,
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'general' },
          {
            path: 'general',
            loadComponent: () =>
              import(
                './pages/catalogs/currencies/currencies/currency-edit/general/currency-edit-general.component'
              ).then(m => m.CurrencyEditGeneralComponent),
          },
          {
            path: 'exchange-rates',
            loadComponent: () =>
              import(
                './pages/catalogs/currencies/currencies/currency-edit/exchange-rates/currency-edit-exchange-rates.component'
              ).then(m => m.CurrencyEditExchangeRatesComponent),
          },
          {
            path: 'files',
            loadComponent: () =>
              import(
                './pages/catalogs/currencies/currencies/currency-edit/files/currency-edit-files.component'
              ).then(m => m.CurrencyEditFilesComponent),
          },
          {
            path: 'action-log',
            loadComponent: () =>
              import(
                './pages/catalogs/currencies/currencies/currency-edit/action-log/currency-edit-action-log.component'
              ).then(m => m.CurrencyEditActionLogComponent),
          },
        ],
      },
      // ── Documentación ─────────────────────────────────────────────────────
      pendingSectionRoute('documentation/general', {
        ...DOC_SECTION,
        sectionLabel: 'General',
        activeMenubarId: 'doc-general',
      }),
      pendingSectionRoute('documentation/business-rules', {
        ...DOC_SECTION,
        sectionLabel: 'Reglas de negocio',
        activeMenubarId: 'doc-business-rules',
      }),
      {
        path: 'documentation/processes',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/documentation/processes/processes.component').then(
            m => m.ProcessesComponent,
          ),
        data: {
          chrome: {
            moduleId: 'documentation',
            activeMenubarId: 'doc-processes',
            title: 'Procesos',
            titleBarMode: 'data-table',
            breadcrumbItems: [
              ADMIN_CRUMB,
              { id: 'documentation', label: 'Documentación' },
              { id: 'processes', label: 'Procesos' },
            ],
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'documentation/processes/:codigo',
        loadComponent: () =>
          import(
            './pages/documentation/processes/process-view/process-view.component'
          ).then(m => m.ProcessViewComponent),
        data: {
          chrome: {
            moduleId: 'documentation',
            activeMenubarId: 'doc-processes',
            title: 'Proceso',
            titleBarMode: 'form',
            breadcrumbItems: [
              ADMIN_CRUMB,
              { id: 'documentation', label: 'Documentación' },
              { id: 'processes', label: 'Procesos' },
            ],
          } satisfies UiPageChromeConfig,
        },
      },
      {
        path: 'documentation/database',
        pathMatch: 'full',
        redirectTo: 'documentation/database/demo/tables',
      },
      {
        path: 'documentation/database/demo',
        loadComponent: () =>
          import(
            './pages/documentation/database/demo-database-admin.component'
          ).then(m => m.DemoDatabaseAdminComponent),
        data: {
          chrome: demoDatabaseChrome('Base de datos demo'),
        },
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'tables' },
          {
            path: 'tables',
            pathMatch: 'full',
            loadComponent: () =>
              import(
                './pages/documentation/database/demo-database-tables.component'
              ).then(m => m.DemoDatabaseTablesComponent),
            data: {
              chrome: demoDatabaseChrome('Tablas', {
                id: 'tables',
                label: 'Tablas',
              }),
            },
          },
          {
            path: 'tables/:tabla/structure',
            pathMatch: 'full',
            loadComponent: () =>
              import(
                './pages/documentation/database/demo-database-table-structure.component'
              ).then(m => m.DemoDatabaseTableStructureComponent),
            data: {
              chrome: demoDatabaseChrome(
                'Estructura',
                { id: 'tables', label: 'Tablas' },
                { id: 'structure', label: 'Estructura' },
              ),
            },
          },
          {
            path: 'tables/:tabla/content',
            pathMatch: 'full',
            loadComponent: () =>
              import(
                './pages/documentation/database/demo-database-table-content.component'
              ).then(m => m.DemoDatabaseTableContentComponent),
            data: {
              chrome: demoDatabaseChrome(
                'Contenido',
                { id: 'tables', label: 'Tablas' },
                { id: 'content', label: 'Contenido' },
              ),
            },
          },
          {
            path: 'migrations',
            pathMatch: 'full',
            loadComponent: () =>
              import(
                './pages/documentation/database/demo-database-migrations.component'
              ).then(m => m.DemoDatabaseMigrationsComponent),
            data: {
              chrome: demoDatabaseChrome('Migraciones', {
                id: 'migrations',
                label: 'Migraciones',
              }),
            },
          },
          {
            path: 'seeds',
            pathMatch: 'full',
            loadComponent: () =>
              import(
                './pages/documentation/database/demo-database-seeds.component'
              ).then(m => m.DemoDatabaseSeedsComponent),
            data: {
              chrome: demoDatabaseChrome('Semillas', {
                id: 'seeds',
                label: 'Semillas',
              }),
            },
          },
        ],
      },
      pendingSectionRoute('documentation/back-end', {
        ...DOC_SECTION,
        sectionLabel: 'Back End',
        activeMenubarId: 'doc-backend',
      }),
      pendingSectionRoute('documentation/front-end', {
        ...DOC_SECTION,
        sectionLabel: 'Front End',
        activeMenubarId: 'doc-frontend',
      }),
      pendingSectionRoute('documentation/infrastructure', {
        ...DOC_SECTION,
        sectionLabel: 'Infraestructura',
        activeMenubarId: 'doc-infrastructure',
      }),
      pendingSectionRoute('documentation/security', {
        ...DOC_SECTION,
        sectionLabel: 'Seguridad',
        activeMenubarId: 'doc-security',
      }),
    ],
  },
];
